import React, { useState } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import '../../assets/styles/FormCard.css';

const describedBy = (id, error, hint) => {
  if (error) return `${id}-error`;
  if (hint) return `${id}-hint`;
  return undefined;
};

const fieldClass = (icon, className) =>
  ['tp-field', icon ? 'has-icon' : '', className].filter(Boolean).join(' ');

const FieldLabel = ({ id, label, hideLabel }) =>
  label ? (
    <label htmlFor={id} className={hideLabel ? 'tp-sr-only' : undefined}>{label}</label>
  ) : null;

const FieldIcon = ({ icon }) =>
  icon ? <span className="tp-field__icon" aria-hidden="true">{icon}</span> : null;

const FieldMessages = ({ id, error, hint }) => (
  <>
    {hint && !error && <p id={`${id}-hint`} className="tp-field__hint">{hint}</p>}
    {error && <p id={`${id}-error`} className="tp-field__error">{error}</p>}
  </>
);

const FormField = ({
  id,
  label,
  hideLabel = false,
  icon,
  error,
  hint,
  as: Control = 'input',
  className = '',
  ...controlProps
}) => {
  const control = (
    <Control
      id={id}
      className={error ? 'has-error' : undefined}
      aria-invalid={error ? true : undefined}
      aria-describedby={describedBy(id, error, hint)}
      {...controlProps}
    />
  );

  return (
    <div className={fieldClass(icon, className)}>
      <FieldLabel id={id} label={label} hideLabel={hideLabel} />
      {icon ? (
        <div className="tp-field__control">
          <FieldIcon icon={icon} />
          {control}
        </div>
      ) : control}
      <FieldMessages id={id} error={error} hint={hint} />
    </div>
  );
};

const PasswordField = ({
  id,
  label,
  hideLabel = false,
  icon,
  error,
  hint,
  showLabel,
  hideLabelText,
  className = '',
  ...inputProps
}) => {
  const [visible, setVisible] = useState(false);

  return (
    <div className={fieldClass(icon, className)}>
      <FieldLabel id={id} label={label} hideLabel={hideLabel} />
      <div className="tp-field__control has-toggle">
        <FieldIcon icon={icon} />
        <input
          id={id}
          type={visible ? 'text' : 'password'}
          className={error ? 'has-error' : undefined}
          aria-invalid={error ? true : undefined}
          aria-describedby={describedBy(id, error, hint)}
          {...inputProps}
        />
        <button
          type="button"
          className="tp-field__toggle"
          onClick={() => setVisible((current) => !current)}
          aria-label={visible ? hideLabelText : showLabel}
          aria-pressed={visible}
        >
          {visible ? <EyeOff size={18} strokeWidth={1.75} /> : <Eye size={18} strokeWidth={1.75} />}
        </button>
      </div>
      <FieldMessages id={id} error={error} hint={hint} />
    </div>
  );
};

export default FormField;
export { FormField, PasswordField };
